import React, { useEffect, useState } from 'react';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Wifi, Landmark, AlertTriangle } from 'lucide-react';
import { useLang } from '../i18n';
import { buildWifiPayload, buildEpcPayload, isValidIban } from '../qrPayloads';

// Structured input forms for the special QR content types (Wi-Fi + SEPA bank
// transfer). Reports the built payload string (and a friendly file name) to the
// parent via onPayload(payload, name).
export const QrContentPanel = ({ contentType, onPayload }) => {
  const { t } = useLang();
  const [wifi, setWifi] = useState({ ssid: '', password: '', encryption: 'WPA', hidden: false });
  const [bank, setBank] = useState({ name: '', iban: '', bic: '', amount: '', purpose: '' });

  const updWifi = (patch) => setWifi((w) => ({ ...w, ...patch }));
  const updBank = (patch) => setBank((b) => ({ ...b, ...patch }));

  useEffect(() => {
    if (contentType !== 'wifi') return;
    const payload = buildWifiPayload(wifi);
    onPayload(payload, wifi.ssid ? `wlan_${wifi.ssid}` : 'wlan', {
      kind: 'wifi',
      ssid: wifi.ssid,
      password: wifi.encryption === 'nopass' ? '' : wifi.password,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, wifi]);

  useEffect(() => {
    if (contentType !== 'bank') return;
    const payload = buildEpcPayload(bank);
    onPayload(payload, bank.name ? `giro_${bank.name}` : (bank.iban ? `giro_${bank.iban}` : 'giro'), { kind: 'bank' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contentType, bank]);

  if (contentType === 'wifi') {
    return (
      <div className="space-y-4" data-testid="qr-wifi-form">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Wifi className="h-4 w-4 text-primary" />{t('content_wifi')}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('wifi_ssid')}</Label>
          <Input
            value={wifi.ssid}
            onChange={(e) => updWifi({ ssid: e.target.value })}
            placeholder="MyNetwork"
            data-testid="wifi-ssid-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('wifi_encryption')}</Label>
          <Select value={wifi.encryption} onValueChange={(v) => updWifi({ encryption: v })}>
            <SelectTrigger aria-label={t('wifi_encryption')} data-testid="wifi-encryption-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="WPA">{t('enc_wpa')}</SelectItem>
              <SelectItem value="WEP">{t('enc_wep')}</SelectItem>
              <SelectItem value="nopass">{t('enc_none')}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {wifi.encryption !== 'nopass' && (
          <div className="space-y-1.5">
            <Label className="text-sm">{t('wifi_password')}</Label>
            <Input
              value={wifi.password}
              onChange={(e) => updWifi({ password: e.target.value })}
              placeholder="••••••••"
              data-testid="wifi-password-input"
            />
          </div>
        )}
        <div className="flex items-center justify-between rounded-lg border bg-secondary/40 px-3 py-2.5">
          <Label htmlFor="wifi-hidden" className="text-sm">{t('wifi_hidden')}</Label>
          <Switch
            id="wifi-hidden"
            checked={wifi.hidden}
            onCheckedChange={(v) => updWifi({ hidden: v })}
            data-testid="wifi-hidden-switch"
          />
        </div>
        <p className="text-xs text-muted-foreground">{t('wifi_hint')}</p>
      </div>
    );
  }

  if (contentType === 'bank') {
    return (
      <div className="space-y-4" data-testid="qr-bank-form">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Landmark className="h-4 w-4 text-primary" />{t('content_bank')}
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('bank_name')}</Label>
          <Input
            value={bank.name}
            onChange={(e) => updBank({ name: e.target.value })}
            placeholder="Max Mustermann"
            data-testid="bank-name-input"
          />
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('bank_iban')}</Label>
          <Input
            value={bank.iban}
            onChange={(e) => updBank({ iban: e.target.value })}
            placeholder="DE89 3704 0044 0532 0130 00"
            className="font-mono"
            data-testid="bank-iban-input"
          />
          {bank.iban.replace(/\s+/g, '').length >= 15 && !isValidIban(bank.iban) && (
            <p className="flex items-center gap-1.5 text-xs text-amber-600" data-testid="bank-iban-warning">
              <AlertTriangle className="h-3.5 w-3.5 shrink-0" />{t('iban_invalid')}
            </p>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-sm">{t('bank_bic')}</Label>
            <Input
              value={bank.bic}
              onChange={(e) => updBank({ bic: e.target.value })}
              placeholder="COBADEFFXXX"
              className="font-mono"
              data-testid="bank-bic-input"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-sm">{t('bank_amount')}</Label>
            <Input
              value={bank.amount}
              onChange={(e) => updBank({ amount: e.target.value })}
              placeholder="12,50"
              inputMode="decimal"
              data-testid="bank-amount-input"
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-sm">{t('bank_purpose')}</Label>
          <Input
            value={bank.purpose}
            onChange={(e) => updBank({ purpose: e.target.value })}
            placeholder={t('bank_purpose')}
            maxLength={140}
            data-testid="bank-purpose-input"
          />
        </div>
        <p className="text-xs text-muted-foreground">{t('bank_hint')}</p>
      </div>
    );
  }

  return null;
};

export default QrContentPanel;
